import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, priceLabel } from "@/lib/api";
import { Reveal, EASE } from "@/components/Reveal";

const HERO_VIDEO_WEBM = "/media/hero-ai-carmine.webm";
const HERO_VIDEO_MP4 = "/media/hero-ai-carmine.mp4";
const HERO_POSTER = "/media/hero-ai-carmine-poster.jpg";
const HERO_MOBILE_VIDEO_WEBM = "/media/hero-ai-carmine-mobile.webm";
const HERO_MOBILE_VIDEO_MP4 = "/media/hero-ai-carmine-mobile.mp4";
const HERO_MOBILE_POSTER = "/media/hero-ai-carmine-mobile-poster.jpg";

const HERO_LINES = ["Capolavori", "del tempo", "e della luce."];

const STORY_CHAPTERS = [
  {
    n: "01",
    title: "Le Origini",
    text: "Orologi Preziosi nasce nei primi anni '80 grazie alla dedizione di Antonio Lucariello, che ha trasformato una profonda passione per l'orologeria e l'arte orafa in un punto di riferimento nel settore del lusso. Fin dagli esordi: qualità, trasparenza e affidabilità.",
    img: "/media/storia-origini.jpg",
  },
  {
    n: "02",
    title: "L'Eredità",
    text: "Nel corso degli anni, la cura nella selezione dei pezzi e l'attenzione alla clientela hanno reso il marchio sinonimo di prestigio e sicurezza. Oggi la storia prosegue con Carmine, figlio del fondatore, che porta avanti l'eredità familiare con lo stesso spirito di competenza e passione.",
    img: "/media/carmine-daytona.jpg",
  },
  {
    n: "03",
    title: "La Specializzazione",
    text: "Siamo specializzati nella vendita di orologi di lusso certificati dei marchi più iconici — Rolex, Patek Philippe, Audemars Piguet e Omega — pezzi selezionati e autenticati, disponibili in pronta consegna.",
    img: "/media/storia-oggi.jpg",
  },
];

const STORY_STATS = [
  { value: "40+", label: "Anni di attività" },
  { value: "4.500+", label: "Orologi venduti" },
  { value: "12", label: "Mesi di garanzia" },
];

const MARQUEE_ITEMS = [
  "Orologi Certificati",
  "Garantiti 12 Mesi",
  "Spedizione in Tutto il Mondo",
  "I Migliori Marchi",
  "Napoli",
  "Da oltre 40 Anni",
];

function CollectionSection({ eyebrow, title, items, viewAllTo, viewAllTestId, testId, cardPrefix }) {
  if (items.length === 0) return null;
  return (
    <section className="border-t border-line px-5 py-16 sm:px-6 lg:px-12 lg:py-28" data-testid={testId}>
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">{eyebrow}</p>
            <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-ivory lg:text-6xl">
              {title}
              <span className="italic text-gold">.</span>
            </h2>
          </div>
          <Link
            to={viewAllTo}
            data-testid={viewAllTestId}
            className="group inline-flex items-center gap-3 border-b border-line pb-2 text-xs uppercase tracking-[0.25em] text-muted transition-colors duration-500 hover:border-gold hover:text-gold"
          >
            Vedi tutti
            <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Reveal>

      <div className="mt-16 grid gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-4" data-testid={`${testId}-grid`}>
        {items.map((p, i) => (
          <Reveal key={p.id} delay={(i % 4) * 0.08}>
            <Link to={`/prodotto/${p.id}`} data-testid={`${cardPrefix}-${p.reference}`} className="group block">
              <div className="spotlight relative overflow-hidden bg-elevated">
                <img
                  src={p.image}
                  alt={p.name}
                  className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                    p.category === "orologi" ? "aspect-square sm:aspect-[4/5]" : "aspect-[4/5]"
                  }`}
                />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-gold">
                {p.reference.startsWith("OEP-") ? p.reference : `Ref. ${p.reference}`}
                {p.year ? ` · ${p.year}` : ""}
              </p>
              <h3 className="mt-1 font-serif text-xl leading-tight text-ivory">{p.name}</h3>
              <div className="mt-1 flex items-baseline justify-between gap-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{p.condition}</p>
                <p className="font-mono text-sm text-gold">{priceLabel(p)}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ValutazioneForm() {
  const [form, setForm] = useState({ name: "", contact: "", watch: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const field =
    "min-h-12 w-full border border-line bg-transparent px-4 py-3.5 text-base text-ivory outline-none transition-colors duration-300 placeholder:text-muted/60 focus:border-gold";
  const label = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/valutazioni", form);
      setSent(true);
    } catch {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-gold/40 bg-surface p-10 text-center" data-testid="valutazione-success">
        <p className="font-serif text-3xl italic text-ivory">Richiesta inviata.</p>
        <p className="mt-4 text-sm font-light leading-relaxed text-muted">
          Grazie per averci contattato, le risponderemo prima possibile
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-line bg-surface p-8 lg:p-10" data-testid="valutazione-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={label}>Nome e Cognome</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="valutazione-name-input" placeholder="Mario Rossi" />
        </div>
        <div>
          <label className={label}>Email o Telefono</label>
          <input required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={field} data-testid="valutazione-contact-input" placeholder="mario@email.it" />
        </div>
      </div>
      <div className="mt-5">
        <label className={label}>Marca e Modello dell'orologio</label>
        <input required value={form.watch} onChange={(e) => setForm({ ...form, watch: e.target.value })} className={field} data-testid="valutazione-watch-input" placeholder="Es. Rolex Datejust 16233" />
      </div>
      <div className="mt-5">
        <label className={label}>Note (anno, condizioni, scatola e garanzia)</label>
        <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} data-testid="valutazione-message-input" placeholder="Raccontaci il tuo orologio…" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="valutazione-submit-button"
        className="mt-7 w-full border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void disabled:opacity-50"
      >
        {sending ? "Invio in corso…" : "Richiedi Valutazione Gratuita"}
      </button>
      <p className="mt-4 text-center text-[11px] text-muted">
        Preferisci parlare? <a href="https://wa.me/393511997843" target="_blank" rel="noreferrer" className="text-gold" data-testid="valutazione-whatsapp-link">Scrivici su WhatsApp</a>
      </p>
    </form>
  );
}

export default function Home() {
  const [watches, setWatches] = useState([]);
  const [jewels, setJewels] = useState([]);
  const [mobileHero, setMobileHero] = useState(() => window.matchMedia("(max-width: 640px)").matches);
  const reduceMotion = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 640px)");
    const update = () => setMobileHero(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    api.get("/products", { params: { category: "orologi" } }).then(({ data }) => setWatches(data)).catch(() => {});
    api.get("/products", { params: { category: "gioielli" } }).then(({ data }) => setJewels(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative h-[100svh] overflow-hidden bg-void p-0 sm:p-3 lg:p-5">
        <motion.div style={mobileHero || reduceMotion ? {} : { y: bgY, scale: bgScale }} className="absolute inset-0 sm:inset-3 sm:overflow-hidden sm:rounded-xl sm:bg-elevated sm:shadow-[0_60px_160px_-40px_rgba(0,0,0,0.9)] sm:ring-1 sm:ring-white/5 lg:inset-5">
          {reduceMotion ? (
            <img
              src={mobileHero ? HERO_MOBILE_POSTER : HERO_POSTER}
              alt=""
              aria-hidden="true"
              data-testid="hero-background-poster"
              className="pointer-events-none h-full w-full bg-void object-cover object-center lg:object-[50%_0%]"
            />
          ) : (
            <video
              key={mobileHero ? "mobile" : "desktop"}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={mobileHero ? HERO_MOBILE_POSTER : HERO_POSTER}
              data-testid="hero-background-video"
              aria-hidden="true"
              className="pointer-events-none h-full w-full bg-void object-cover object-center [filter:contrast(1.05)_saturate(1.06)_brightness(1.12)] lg:object-[50%_0%]"
            >
              <source src={HERO_MOBILE_VIDEO_WEBM} media="(max-width: 640px)" type="video/webm" />
              <source src={HERO_MOBILE_VIDEO_MP4} media="(max-width: 640px)" type="video/mp4" />
              <source src={HERO_VIDEO_WEBM} type="video/webm" />
              <source src={HERO_VIDEO_MP4} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-black/10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,5,0.35)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-void via-void/75 to-transparent sm:h-3/5 sm:from-void sm:via-void/45" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="absolute inset-x-0 bottom-5 z-10 flex flex-col px-7 sm:relative sm:bottom-auto sm:h-full sm:justify-end sm:px-9 sm:pb-24 lg:px-14">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="order-2 mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gold [text-shadow:0_1px_12px_rgba(0,0,0,0.9)] sm:order-none sm:mt-0 sm:text-[11px] sm:tracking-[0.4em]"
          >
            Rivendita · Napoli · Da oltre 40 anni
          </motion.p>
          <h1 className="order-1 mt-0 font-serif text-3xl font-light leading-[0.95] tracking-tighter text-ivory [text-shadow:0_2px_18px_rgba(0,0,0,0.85)] sm:order-none sm:mt-6 sm:text-7xl lg:text-8xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.4 + i * 0.16, ease: EASE }}
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="order-3 mt-4 max-w-md text-sm font-light leading-relaxed text-muted [text-shadow:0_1px_14px_rgba(0,0,0,0.75)] sm:order-none sm:mt-8 sm:text-base"
          >
            Orologi certificati e garantiti 12 mesi, selezionati tra i migliori marchi dell'orologeria di prestigio.
            Spediamo in tutto il mondo.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: EASE }}
            className="order-4 mt-6 flex flex-wrap items-center gap-5 sm:order-none sm:mt-10 sm:gap-6"
          >
            <Link
              to="/catalogo?categoria=orologi"
              data-testid="hero-cta-catalogo"
              className="border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void"
            >
              Esplora la Collezione
            </Link>
            <a
              href="#storia"
              data-testid="hero-cta-storia"
              className="group flex min-h-11 items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted transition-colors duration-500 hover:text-ivory"
            >
              La Nostra Storia
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-6 z-10 hidden font-mono text-[10px] uppercase tracking-[0.3em] text-muted lg:right-12 lg:block"
        >
          Scorri ↓
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-line bg-void py-7" data-testid="editorial-marquee">
        <div className="flex w-max animate-marquee whitespace-nowrap">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center">
              {MARQUEE_ITEMS.map((item, i) => (
                <span key={i} className="flex items-center font-serif text-2xl italic text-gold/60 md:text-3xl">
                  <span className="px-8">{item}</span>
                  <span className="text-sm text-gold/40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* LA NOSTRA STORIA */}
      <section id="storia" className="px-5 py-16 sm:px-6 lg:px-12 lg:py-32" data-testid="storia-section">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">La Nostra Storia</p>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight text-ivory lg:text-6xl">
            Una famiglia, una passione: <span className="italic text-gold">il tempo.</span>
          </h2>
        </Reveal>

        <div className="mt-16 space-y-20 lg:mt-24 lg:space-y-32">
          {STORY_CHAPTERS.map((c, i) => (
            <div key={c.n} className="grid items-center gap-8 lg:grid-cols-12 lg:gap-10" data-testid={`storia-chapter-${c.n}`}>
              <Reveal className={i % 2 === 0 ? "lg:col-span-5" : "lg:col-span-5 lg:col-start-8 lg:order-2"}>
                <div className="spotlight relative overflow-hidden bg-elevated">
                  {c.videoWebm ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      poster={c.poster}
                      data-testid={`storia-video-${c.n}`}
                      aria-label={c.title}
                      className="aspect-[4/3] w-full object-cover object-[62%_50%]"
                    >
                      <source src={c.videoWebm} type="video/webm" />
                      <source src={c.videoMp4} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={c.img} alt={c.title} className="aspect-[4/3] w-full object-cover" />
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.15} className={i % 2 === 0 ? "lg:col-span-6 lg:col-start-7" : "lg:col-span-6 lg:order-1"}>
                <p className="font-serif text-7xl font-light text-gold/25 lg:text-8xl">{c.n}</p>
                <h3 className="mt-2 font-serif text-3xl italic text-ivory lg:text-4xl">{c.title}</h3>
                <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted">{c.text}</p>
              </Reveal>
            </div>
          ))}
        </div>

        <Reveal className="mt-20 lg:mt-32">
          <div className="grid gap-8 border-y border-line py-10 sm:grid-cols-3 lg:gap-10 lg:py-14" data-testid="storia-stats">
            {STORY_STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-4xl font-light text-gold lg:text-7xl">{s.value}</p>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted">{s.label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* OROLOGI */}
      <CollectionSection
        eyebrow="In Pronta Consegna"
        title="Orologi"
        items={watches}
        viewAllTo="/catalogo?categoria=orologi"
        viewAllTestId="home-orologi-view-all"
        testId="home-orologi-section"
        cardPrefix="home-watch"
      />

      {/* GIOIELLI */}
      <CollectionSection
        eyebrow="I Preziosi"
        title="Gioielli"
        items={jewels}
        viewAllTo="/catalogo?categoria=gioielli"
        viewAllTestId="home-gioielli-view-all"
        testId="home-gioielli-section"
        cardPrefix="home-jewel"
      />

      {/* VALUTAZIONE */}
      <section id="valutazione" className="border-t border-line px-5 py-16 sm:px-6 lg:px-12 lg:py-32" data-testid="valutazione-section">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">Valutazione Orologi</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-ivory lg:text-6xl">
              Vendi o valuta <span className="italic text-gold">il tuo orologio.</span>
            </h2>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-muted">
              Acquistiamo, permutiamo e valutiamo il tuo orologio, sia nuovo che mai indossato, garantendo sempre
              condizioni trasparenti e competitive.
            </p>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted">
              Da oltre quarant'anni operiamo nel settore dell'orologeria di prestigio, offrendo competenza, serietà e
              un servizio curato in ogni dettaglio.
            </p>
            <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-muted">
              <p>+39 351 199 7843</p>
              <p>carminelucariello@yahoo.it</p>
              <p>Via Diomede Carafa 62, Napoli</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ValutazioneForm />
          </Reveal>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="border-t border-line px-5 py-16 text-center sm:px-6 lg:px-12 lg:py-32" data-testid="final-cta">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">Atelier · Via Diomede Carafa 62, Napoli</p>
          <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight text-ivory lg:text-6xl">
            Il prossimo capitolo <span className="italic text-gold">lo scrivete voi.</span>
          </h2>
          <Link
            to="/catalogo?categoria=orologi"
            data-testid="final-cta-button"
            className="mt-12 inline-block border border-gold px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void"
          >
            Scopri la Collezione
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
